import { useEffect, useMemo, useRef } from "react";
import { useAvailableLinesStore } from "../../stores/hashs/useAvailableLinesStore";
import { useDateStore } from "../../stores/useDateStore";
import { useOperationalMapStore } from "../../stores/useOperationalMapStore";
import { useTripsStore } from "../../stores/useTripsStore";
import { useVehiclesStore, NO_VEHICLE_ID } from "../../stores/useVehiclesStore";
import { useDriversStore, NO_DRIVER_ID } from "../../stores/useDriversStore";
import { useEscaletedLinesTodayStore } from "../../stores/hashs/useEscaletedLinesTodayStore";
import { useHourFilterStore } from "../../stores/useHourFilterStore";
import { useTripAnalysisSubscriptionStore } from "../../stores/analysis/useTripAnalysisSubscriptionStore";
import { m, isTripInHourRange } from "../../utils/dateUtils";
import { useTripStatusAnalysisStore } from "../../stores/analysis/useTripStatusAnalysisStore";
import { useTripFiltersStore } from "../../stores/useTripFiltersStore";
import { getTripKey } from "../../utils/tripAnalysis";
import resolveTripStatus from "../../utils/tripStatus";

export function useEscaletedLinesToday() {

    const {
        data: availableLines,
        totalToday,
        totalNext
    } = useAvailableLinesStore();

    const hourRange = useHourFilterStore(state => state.hourRange);

    const today = useDateStore(state => state.today);
    const tomorrow = useDateStore(state => state.tomorrow);
    const selectedDate = useDateStore(state => state.selectedDate);
    const currentDayLabel = useDateStore(state => state.currentDayLabel);
    const currentDayShortLabel = useDateStore(state => state.currentDayShortLabel);
    const nextDayShortLabel = useDateStore(state => state.nextDayShortLabel);


    const tripsMap = useTripsStore(state => state.tripsMap);

    const allowVeiculosEmViagem =
        useVehiclesStore(state => state.allowVeiculosEmViagem);

    const allowDriversEmViagem = 
        useDriversStore(state => state.allowDriversEmViagem);

    const setEscaletedLinesToday =
        useEscaletedLinesTodayStore(
            state => state.setEscaletedLinesToday
        );

    const statusMap = useTripStatusAnalysisStore(state => state.statusMap);

    const situacoesSet = useTripFiltersStore(state => state.situacoesSet);

    const desempenhosSet = useTripFiltersStore(state => state.desempenhosSet);

    const isOperationalMapMode = useOperationalMapStore(
        state => state.isOperationalMapMode
    );

    const operationalMapDateRange = useOperationalMapStore(
        state => state.operationalMapDateRange
    );

    const base = useMemo(() => {
        
        const data = [];

        // =========================
        // MAPA OPERACIONAL
        // =========================

        if (isOperationalMapMode && operationalMapDateRange) {

            const linesMap = new Map(
                availableLines.map(linha => [
                    String(linha.CODIGO_INTEGRACAO),
                    linha
                ])
            );

            const {
                startDate,
                endDate
            } = operationalMapDateRange;

            for (const trip of tripsMap.values()) {

                const tripDate = m(trip.DATA_REFERENCIA);

                if (
                    tripDate.isBefore(startDate, 'day') ||
                    tripDate.isAfter(endDate, 'day')
                ) {
                    continue;
                }

                const linha = linesMap.get(String(trip.CODIGO_INTEGRACAO));

                if(!linha) {
                    continue;
                }

                const vehicleId = String(
                    trip?.license_plate || NO_VEHICLE_ID
                ).trim();

                if (
                    allowVeiculosEmViagem &&
                    !allowVeiculosEmViagem.has(
                        String(vehicleId)
                    )
                ) {
                    continue;
                }

                const driverId = String(
                    trip?.driver_id || NO_DRIVER_ID
                ).trim();

                if (
                    allowDriversEmViagem &&
                    !allowDriversEmViagem.has(
                        String(driverId)
                    )
                ) {
                    continue;
                }

                data.push({
                    ...linha,
                    ...trip
                });
            }

            return data;
        }

        // =========================
        // TORRE
        // =========================

        for (const linha of availableLines) {
            
            const codigo = String(linha.CODIGO_INTEGRACAO);

            const trips = [];

            if (linha.isEscaladaToday) {

                const trip = tripsMap.get(
                    `${today}-${codigo}`
                );

                if (trip) {
                    trips.push(trip);
                }

            }

            if (linha.isEscaladaNext) {

                const trip = tripsMap.get(
                    `${tomorrow}-${codigo}`
                );

                if (trip) {
                    trips.push(trip);
                }

            }

            for (const trip of trips) {

                if (!isTripInHourRange(
                    trip,
                    hourRange,
                    selectedDate
                )) {
                    continue;
                }

                const vehicleId = String(
                    trip?.license_plate || NO_VEHICLE_ID
                ).trim();

                if (
                    allowVeiculosEmViagem &&
                    !allowVeiculosEmViagem.has(
                        String(vehicleId)
                    )
                ) {
                    continue;
                }

                const driverId = String(
                    trip?.driver_id || NO_DRIVER_ID
                ).trim();

                if (
                    allowDriversEmViagem &&
                    !allowDriversEmViagem.has(
                        String(driverId)
                    )
                ) {
                    continue;
                }
                
                data.push({
                    ...linha,
                    ...trip
                });

            }

        }

        return data;

    }, [
        isOperationalMapMode,
        operationalMapDateRange,
        hourRange,
        selectedDate,
        availableLines,
        today,
        tomorrow,
        tripsMap,
        allowVeiculosEmViagem,
        allowDriversEmViagem
    ]);

    const value = useMemo(() => {
        
        const data = [];

        for (const trip of base) {

            const storedStatus = statusMap.get(
                getTripKey(
                    trip.DATA_REFERENCIA,
                    trip.CODIGO_INTEGRACAO
                )
            );

            const status = resolveTripStatus(
                storedStatus,
                trip.DATAHORA_FINAL_PREVISTO
            );

            const situacaoOk = status.situacaoKey;

            if (
                situacoesSet.size &&
                !situacoesSet.has(situacaoOk)
            ) {
                continue;
            }

            const desempenhoOk =
                status?.desempenho == null ||
                situacaoOk === 'agendada' ||
                situacaoOk === 'nao_realizada' ||
                desempenhosSet.has(status.desempenho);

            if (!desempenhoOk) continue;      

            data.push({
                ...trip,
                tripStatus: status
            });

        }

        const total = data.length;

        const includesNextDay = hourRange[1] >= 24;

        let totalDay = 0;
        let totalForPercent = 0;

        // =========================
        // MAPA OPERACIONAL
        // =========================

        if (isOperationalMapMode && operationalMapDateRange) {

            /*
            * No mapa operacional, o próprio resultado filtrado
            * representa o período selecionado.
            *
            * Não usamos totalToday / totalNext porque o range
            * pode conter qualquer quantidade de dias.
            */

            totalDay = total;
            totalForPercent = total;

        } else {

            // =========================
            // TORRE
            // =========================

            /*
            * Denominador:
            * quantidade de linhas originalmente esperadas
            * para hoje e, quando aplicável, D+1.
            */
            totalDay =
                totalToday +
                (includesNextDay ? totalNext : 0);

            /*
            * Numerador:
            * viagens exibidas que realmente pertencem
            * ao conjunto esperado do respectivo dia.
            *
            * Uma linha escalada extraordinariamente aparece
            * na Torre, mas não altera a porcentagem.
            */
            totalForPercent = data.reduce((acc, trip) => {

                const tripDate = m(
                    trip.DATA_REFERENCIA
                ).format('YYYY-MM-DD');

                if (
                    tripDate === today &&
                    trip.today
                ) {
                    return acc + 1;
                }

                if (
                    includesNextDay &&
                    tripDate === tomorrow &&
                    trip.next
                ) {
                    return acc + 1;
                }

                return acc;

            }, 0);
        }

        const dayLabel =
            isOperationalMapMode && operationalMapDateRange
                ? (
                    operationalMapDateRange.startDate.isSame(
                        operationalMapDateRange.endDate,
                        'day'
                    )
                        ? operationalMapDateRange.startDate.format('DD/MM/YYYY')
                        : `${operationalMapDateRange.startDate.format('DD/MM/YYYY')} - ${operationalMapDateRange.endDate.format('DD/MM/YYYY')}`
                )
                : includesNextDay
                    ? `${currentDayShortLabel} / ${nextDayShortLabel}`
                    : currentDayLabel;

        return {
            data,
            total,
            totalDay,

            percentTotal:
                totalDay
                    ? Math.round(
                        totalForPercent / totalDay * 100
                    )
                    : 0,

            title:
                `VIAG${total > 1 ? 'ENS' : 'EM'} DE ${dayLabel}`
        };

    }, [
        base,
        hourRange,
        statusMap,
        situacoesSet,
        desempenhosSet,
        totalToday,
        totalNext,
        today,
        tomorrow,
        currentDayLabel,
        currentDayShortLabel,
        nextDayShortLabel,
        isOperationalMapMode,
        operationalMapDateRange
    ]);

    useEffect(() => {

        setEscaletedLinesToday(value);

    }, [
        value,
        setEscaletedLinesToday
    ]);

    const subscriptionKey = useMemo(() => {

        return base
            .map(trip =>
                getTripKey(
                    trip.DATA_REFERENCIA,
                    trip.CODIGO_INTEGRACAO
                )
            )
            .join('|');

    }, [base]);

    const baseRef = useRef(base);

    baseRef.current = base;

    useEffect(() => {

        useTripAnalysisSubscriptionStore
            .getState()
            .syncSubscriptions(baseRef.current);

    }, [subscriptionKey]);

}